import { rest, RestHandler } from 'msw'
import signinData from '../seed/auth/signin.json'
import interactionData from '../seed/payment/interaction.json'
import clientData from '../seed/payment/client.json'
import quickActionsData from '../seed/profile/quick-actions.json'
import paymentMethodsData from '../seed/payment/payment-methods.json'
import paymentOptionsData from '../seed/payment/payment-options.json'
import paymentStepperData from '../seed/payment/payment-stepper.json'
import paymentStepAdvanceData from '../seed/payment/payment-step-advance.json'
import paymentMethodPostbackData from '../seed/payment/payment-method-postback.json'
import statementData from '../seed/statement/statement.json'
import statementInteractionData from '../seed/statement/interaction.json'
import statementClientData from '../seed/statement/client.json'
import profileData from '../seed/profile/profile.json'
import paymentActivityData from '../seed/profile/payment-activity.json'
import paymentPlansData from '../seed/profile/payment-plans.json'
import paymentActionsData from '../seed/profile/payment-actions.json'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { FAKE_JWT, FAKE_FLEX_SDK, FAKE_ACCEPT_JS } = require('./payment-fakes')

// Shared MSW request handlers for both Jest (Node server) and Playwright (browser service worker).
// Add API mock handlers here as test coverage grows — all slices draw from this single list.
//
// Registration order matters for glob patterns (MSW first-match-wins):
//   */quickactions* must appear before */actions* — every quickactions URL also matches
//   the broader */actions* pattern, so order is load-bearing here.
// Regex patterns are non-overlapping and order-independent:
//   /paymentmethod/ never matches /payment(?!method|plan|activity)/ and vice versa.
//   /checkout\/1/ never matches /checkout(?!\/1)/ and vice versa.
// Playwright page.route() is last-registered-wins; patterns are disjoint there too.
//
// SDK scripts are handled here (not just in Playwright page.route()) because MSW's service
// worker intercepts <script> tag network requests before Playwright's CDP-level interception
// sees them. Without MSW handlers, the service worker passes through to the real network
// (testmicroform.example.com / authorize.net), which fails in tests.
export const handlers: RestHandler[] = [
  // Third-party SDK scripts
  rest.get('https://testmicroform.example.com/*', (_req, res, ctx) =>
    res(ctx.set('Content-Type', 'application/javascript'), ctx.body(FAKE_FLEX_SDK))
  ),
  rest.get('https://jstest.authorize.net/v1/Accept.js', (_req, res, ctx) =>
    res(ctx.set('Content-Type', 'application/javascript'), ctx.body(FAKE_ACCEPT_JS))
  ),
  rest.get('https://js.authorize.net/v1/Accept.js', (_req, res, ctx) =>
    res(ctx.set('Content-Type', 'application/javascript'), ctx.body(FAKE_ACCEPT_JS))
  ),

  // Auth slice — GenericLoginView public shell
  rest.get('*/signin*', (_req, res, ctx) => res(ctx.json(signinData))),

  // Payment slice — leaf endpoints before broad matchers
  rest.get('*/interaction*', (_req, res, ctx) => res(ctx.json(interactionData))),
  rest.get('*/client*', (_req, res, ctx) => res(ctx.json(clientData))),
  rest.get('*/quickactions*', (_req, res, ctx) => res(ctx.json(quickActionsData))),
  rest.get('*/paymentmethod*', (_req, res, ctx) => {
    const data = JSON.parse(JSON.stringify(paymentMethodsData))
    ;(data.newPaymentMethods as Array<{ tokenizer?: { authentication?: string } }>).forEach(m => {
      if (m.tokenizer) m.tokenizer.authentication = FAKE_JWT
    })
    return res(ctx.json(data))
  }),
  // Excludes paymentmethod, paymentplan, paymentactivity (handled separately below)
  rest.get(/\/payment(?!method|plan|activity)/, (_req, res, ctx) => res(ctx.json(paymentOptionsData))),
  rest.post(/\/checkout\/1/, (_req, res, ctx) => res(ctx.json(paymentStepAdvanceData))),
  rest.post(/\/checkout(?!\/1)/, (_req, res, ctx) => res(ctx.json(paymentStepperData))),
  rest.post('*/paymentmethod*', (_req, res, ctx) => res(ctx.json(paymentMethodPostbackData))),

  // Statement slice
  rest.get('*/statement*', (_req, res, ctx) => res(ctx.json(statementData))),

  // Profile slice — leaf endpoints before broad matchers
  rest.get('*/paymentactivity*', (_req, res, ctx) => res(ctx.json(paymentActivityData))),
  rest.get('*/paymentplan*', (_req, res, ctx) => res(ctx.json(paymentPlansData))),
  rest.get('*/actions*', (_req, res, ctx) => res(ctx.json(paymentActionsData))),
  rest.get('*/profile*', (_req, res, ctx) => res(ctx.json(profileData))),
]

// Statement-scoped overrides. Statement Jest tests call server.use(...statementContextHandlers)
// to activate these for their test scope — server.use() prepends handlers, so these match
// before the base */interaction* and */client* entries above.
export const statementContextHandlers: RestHandler[] = [
  rest.get('*/interaction*', (_req, res, ctx) => res(ctx.json(statementInteractionData))),
  rest.get('*/client*', (_req, res, ctx) => res(ctx.json(statementClientData))),
]
